import React, { FormEvent, ReactNode, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { fitHappensSupabase, isFitHappensSupabaseConfigured } from '../../lib/fitHappensSupabase';
import './FitHappensAuthGate.css';

type Props = { children: (user: User, sessionBar: ReactNode) => ReactNode };

export default function FitHappensAuthGate({ children }: Props) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [signup, setSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isFitHappensSupabaseConfigured) { setLoading(false); return; }
    fitHappensSupabase.auth.getSession().then(({ data }) => { setSession(data.session); setLoading(false); });
    const { data } = fitHappensSupabase.auth.onAuthStateChange((_event, next) => { setSession(next); setLoading(false); });
    return () => data.subscription.unsubscribe();
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setSubmitting(true); setMessage('');
    const result = signup
      ? await fitHappensSupabase.auth.signUp({ email, password, options: { emailRedirectTo: new URL('/projects/wardrobe', window.location.origin).toString() } })
      : await fitHappensSupabase.auth.signInWithPassword({ email, password });
    setMessage(result.error?.message || (signup ? 'Check your email to confirm your account.' : 'Welcome back.'));
    setSubmitting(false);
  };

  if (loading) return <div className="fit-auth"><section><p>FIT HAPPENS</p><h1>Loading your wardrobe…</h1></section></div>;
  if (!isFitHappensSupabaseConfigured) return <div className="fit-auth"><section><p>FIT HAPPENS</p><h1>Supabase setup required</h1><p>Add <code>REACT_APP_FIT_HAPPENS_SUPABASE_ANON_KEY</code> to the environment.</p></section></div>;
  if (session?.user) return <>{children(session.user, <div className="fit-session"><span>{session.user.email}</span><button onClick={() => fitHappensSupabase.auth.signOut()}>Sign out</button></div>)}</>;

  return <div className="fit-auth"><section><p>FIT HAPPENS</p><h1>{signup ? 'Create your wardrobe' : 'Welcome back'}</h1><span>What to wear? Consider it handled.</span><form onSubmit={submit}><label>Email<input type="email" required value={email} onChange={e => setEmail(e.target.value)} /></label><label>Password<input type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} /></label><button disabled={submitting}>{submitting ? 'Please wait…' : signup ? 'Create account' : 'Sign in'}</button></form>{message && <div role="status">{message}</div>}<button className="fit-auth-switch" onClick={() => { setSignup(!signup); setMessage(''); }}>{signup ? 'Already have an account? Sign in' : 'New here? Create an account'}</button></section></div>;
}
